import { CommonForm } from '@/components/CommonForm';
import { CommonCheckbox } from '@/components/CommonForm/CommonCheckbox';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonPhoneInput, isPhoneNumberComplete } from '@/components/CommonForm/CommonPhoneInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { BaseSubmit } from '@/components/Form/base';
import { defaultContactNumber } from '@/constants/settings';
import { InventoryFormContext } from '@/contexts/InventoryFormContext';
import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents, isFormDynamicsLocationEnabled } from '@/tools/forms';
import { getUnlockPriceLabel } from '@/tools/inventory';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { faArrowRight, faArrowUpRight, faCheck, faLocationDot, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parsePhone } from '@lamesarv-sdk/tools';
import { IBasicInventory, InventoryField, PropsWithTags } from '@lamesarv-sdk/types';
import Link from 'next/link';
import { Fragment, useContext, useEffect } from 'react';
import { FieldError, get, useFormContext } from 'react-hook-form';
import { IFormSchema, validationSchema } from './schema';

interface FormResetterProps {
  wasSuccessful: boolean;
}

const FormResetter = ({ wasSuccessful }: FormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

interface OptInCheckboxProps {
  nameDependency: string;
  name?: string;
  htmlContent: string;
}

const OptInCheckbox = ({ htmlContent, name, nameDependency }: OptInCheckboxProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const errorDependency = get(errors, nameDependency) as FieldError | null;
  const fieldValue = isPhoneNumberComplete(watch(nameDependency));

  const isValid = fieldValue && !errorDependency;

  if (!isValid) {
    return null;
  }

  return (
    <CommonCheckbox name={name || 'smsOptIn'} className="items-start">
      <div
        dangerouslySetInnerHTML={{
          __html: htmlContent,
        }}
      />
    </CommonCheckbox>
  );
};

const formCaptchaErrorMessage = () => {
  return (
    <div className="w-full lg:w-[46rem] max-md:mb-28 mx-auto flex flex-col gap-4 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center justify-center">
        <span>Form validation failed.</span>
        <span className="flex flex-row gap-2 items-center">
          Please Call{' '}
          <a href="tel:877-641-8052" className="flex gap-2 items-center decoration-dashed underline underline-offset-4">
            <FontAwesomeIcon icon={faPhone} className="size-3.5" />
            <span>877-641-8052</span>
          </a>
        </span>
        <span>for your inquiry</span>
      </div>
    </div>
  );
};

const formGeneralErrorMessage = () => {
  return (
    <div className="w-full lg:w-[46rem] max-md:mb-28 mx-auto flex flex-col gap-4 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center justify-center">
        <span>Form submission failed.</span>
        <span className="flex flex-row gap-2 items-center">
          Please Call{' '}
          <a href="tel:877-641-8052" className="flex gap-2 items-center decoration-dashed underline underline-offset-4">
            <FontAwesomeIcon icon={faPhone} className="size-3.5" />
            <span>877-641-8052</span>
          </a>
        </span>
        <span>for your inquiry</span>
      </div>
    </div>
  );
};

interface InventoryFormProps extends PropsWithTags {
  item: IBasicInventory;
  onSuccess?: () => void;
  locationsOptionSet?: IDynamicsOptionSet;
}

export const InventoryForm = ({ item, onSuccess, locationsOptionSet }: InventoryFormProps) => {
  const {
    context: { inventoryLocations: locations, detailsSettings: settings, defaultContactItem },
  } = useContext(LayoutContext);
  const { closeMobileInquire } = useContext(InventoryFormContext);
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const onSubmit = async (data: IFormSchema, metadata: Record<string, unknown>) => {
    const locationItem = locations.find((location) => location.aliases.includes(data.location.toLowerCase().trim()));

    let zipCode = locationItem?.address?.trim().split(' ').pop() || '';
    if (zipCode.length !== 5) zipCode = '';

    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.seller) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      {
        DataverseFieldName: 'lmrv_stocknumber',
        DataverseFieldValue: item[InventoryField.stockNumber]?.toLowerCase() || '',
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: data.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: data.lastName },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: data.email },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(data.phone) },
      { DataverseFieldName: 'address1_postalcode', DataverseFieldValue: zipCode },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: data.comments },
      {
        DataverseFieldName: 'lmrv_accepttermsandconditions',
        DataverseFieldValue: data.terms ? '753740000' : '753740001',
      },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(locationsOptionSet.find((item) => item.Label === data.location)?.DataverseValue) || '',
      },
    ];

    if (data.subscribe || data.smsOptIn) {
      const topicChannels = [data.subscribe ? 'Email' : undefined, data.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    if (isFormDynamicsLocationEnabled(env, data.location)) {
      await sendForm({
        formKey: 'PDP',
        formName: 'Inventory Unlock Price',
        mappings,
      });
    }
  };

  const { subtext, buttonLabel } = getUnlockPriceLabel(item);

  const contactText = defaultContactItem?.text || defaultContactNumber;
  const contactUrl = defaultContactItem?.url || `tel:${defaultContactNumber}`;

  const location = item[InventoryField.location]
    ? locations?.find((location) => location.aliases.includes(item[InventoryField.location].toLowerCase().trim()))
    : undefined;

  const wasSuccessful = !isLoading && !hasError && submitted;

  useEffect(() => {
    if (wasSuccessful && onSuccess) onSuccess();
  }, [wasSuccessful]);

  return (
    <div className="flex flex-col items-center">
      {location && (
        <>
          <div className="flex items-center gap-1">
            <FontAwesomeIcon icon={faLocationDot} className="size-4 text-black-350 shrink-0" />
            <span className="font-bold text-sm lg:text-base xs:whitespace-nowrap">{location.title}</span>
          </div>

          <a
            target="_blank"
            rel="noreferrer"
            href={
              location?.address ? `https://www.google.com/maps/search/?q=${encodeURIComponent(location.address)}` : '#'
            }
            className="flex items-center gap-1 mt-2.5 hover:underline hover:decoration-blue-500"
          >
            <span className="text-sm text-blue-500 font-semibold">{location.address}</span>
            <FontAwesomeIcon icon={faArrowUpRight} className="size-4 text-blue-500 shrink-0" />
          </a>
        </>
      )}

      <div className="flex gap-2.5 lg:gap-3.5 mt-5">
        <a
          href={contactUrl}
          className="py-1.5 lg:py-2 px-2.5 lg:px-6 flex items-center justify-center gap-1.5 border border-black-200 rounded hover:bg-gray-50"
        >
          <span className="text-xs lg:text-sm text-black-500 whitespace-nowrap">Call us at</span>
          <span className="text-black-500 font-bold text-xs lg:text-sm whitespace-nowrap">{contactText}</span>
        </a>
        <Link
          prefetch
          href={location ? `/${location.route}` : '#'}
          onClick={() => closeMobileInquire()}
          className="py-1.5 lg:py-2 px-2.5 lg:px-6 flex items-center justify-center gap-1.5 border border-black-200 rounded hover:bg-gray-50"
          data-tag="get-directions"
        >
          <span className="text-xs lg:text-sm text-black-500 whitespace-nowrap">Get Directions</span>
          <FontAwesomeIcon icon={faArrowRight} className="size-3.5 lg:size-4 text-black-500" />
        </Link>
      </div>

      <span className="font-bold text-blue-500 mt-2.5 text-sm lg:text-base text-center xs:whitespace-nowrap">
        {subtext}
      </span>

      <CommonForm
        id="formUnlockTodaysPrice"
        schema={validationSchema}
        onSubmit={onSubmit}
        className="flex flex-col gap-4 mt-5 lg:w-[46rem] max-md:mb-28"
        isDisabled={isLoading || wasSuccessful}
        tagGroup="inventory"
        tagItem="lock-price"
        customCaptchaErrorMessage={formCaptchaErrorMessage}
        customErrorMessage={hasError && formGeneralErrorMessage}
      >
        <CommonHidden name="leadType" value="buyer" />
        <CommonHidden name="leadSource" value="lamesarv.com" />
        <FormResetter wasSuccessful={wasSuccessful} />
        <div className="flex items-start gap-6">
          <CommonInput
            label="First Name"
            placeholder="First Name"
            name="firstName"
            isRequired
            classContainer="flex-1"
          />
          <CommonInput label="Last Name" placeholder="Last Name" name="lastName" isRequired classContainer="flex-1" />
        </div>
        <CommonInput label="Email Address" placeholder="example@email.com" name="email" type="email" isRequired />
        <div className="flex flex-col lg:flex-row items-start gap-6">
          <CommonPhoneInput
            label="Phone Number"
            placeholder="Phone Number"
            name="phone"
            isRequired
            classContainer="w-full lg:w-auto lg:flex-1"
          />
          <CommonLocationSelect
            label="Select store location"
            classContainer="w-full lg:w-auto lg:flex-1"
            locationsOptionSet={locationsOptionSet}
            isRequired
          />
        </div>
        <CommonTextarea label="Comments" name="comments" />
        <div className="flex flex-col gap-2 my-1">
          <CommonCheckbox name="terms">
            <div
              dangerouslySetInnerHTML={{
                __html: settings.legalCheckboxContent,
              }}
            />
          </CommonCheckbox>
          {settings.displaySubscribeCheckbox && (
            <CommonCheckbox name="subscribe" className="items-start">
              <div
                dangerouslySetInnerHTML={{
                  __html: settings.subscribeCheckboxContent,
                }}
              />
            </CommonCheckbox>
          )}
          {settings.displaySmsCheckbox && (
            <OptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={settings.customSmsText} />
          )}
        </div>

        {wasSuccessful && (
          <div className="flex items-center gap-2 mt-1">
            <FontAwesomeIcon className="text-green-500" icon={faCheck} />
            <span
              className="text-black-500 font-medium"
              data-tag-group="form-success-label"
              data-tag="form-lock-price-success-label"
            >
              Thank you for submitting the form
            </span>
          </div>
        )}

        {!wasSuccessful && (
          <Fragment>
            <BaseSubmit
              formNameSlug="lock-price"
              submit_button_text={buttonLabel}
              styleClasses="w-full rounded bg-blue-500 text-white font-bold py-3 flex items-center justify-center enabled:hover:bg-blue-600 relative"
            />
          </Fragment>
        )}
      </CommonForm>
    </div>
  );
};
